'use client';

import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button
} from '@mui/material';
import {
  Person as PersonIcon,
  SmartToy as BotIcon,
  Error as ErrorIcon,
  PushPin as PushPinIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

interface ConversationMessage {
  role: 'user' | 'assistant' | 'error';
  content: string;
  timestamp?: string;
  is_error?: boolean;
}

interface ChatMessageProps {
  message: ConversationMessage;
  showTimestamp?: boolean;
  onSaveFact?: (content: string) => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  showTimestamp = false,
  onSaveFact
}) => {
  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return '';
    try {
      return new Date(timestamp).toLocaleString('zh-CN');
    } catch (error) {
      return '';
    }
  };

  const getMessageIcon = () => {
    switch (message.role) {
      case 'user':
        return <PersonIcon />;
      case 'assistant':
        return <BotIcon />;
      case 'error':
        return <ErrorIcon />;
      default:
        return null;
    }
  };

  const getAvatarColor = () => {
    switch (message.role) {
      case 'user':
        return 'primary.main';
      case 'assistant':
        return 'secondary.main';
      case 'error':
        return 'error.main';
      default:
        return 'grey.500';
    }
  };

  const getMessageStyle = () => {
    switch (message.role) {
      case 'user':
        return {
          justifyContent: 'flex-end' as const,
          messageStyle: {
            textOverflow: 'ellipsis',
            p: 2.5,
            borderRadius: '18px 18px 6px 18px',
            width: 'auto',
            bgcolor: 'primary.main',
            color: 'white',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            minWidth: '60px',
            wordBreak: 'normal',
            textAlign: 'left'
          }
        };
      case 'assistant':
        return {
          justifyContent: 'flex-start' as const,
          messageStyle: {
            p: 2.5,
            borderRadius: '18px 18px 18px 6px',
            maxWidth: '80%',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            minWidth: '60px',
            wordBreak: 'normal',
            textAlign: 'left'
          }
        };
      case 'error':
        return {
          justifyContent: 'flex-start' as const,
          messageStyle: {
            p: 2,
            borderRadius: '18px 18px 18px 6px',
            maxWidth: '70%',
            bgcolor: 'error.light',
            color: 'error.contrastText',
            border: '1px solid',
            borderColor: 'error.main'
          }
        };
      default:
        return {
          justifyContent: 'flex-start' as const,
          messageStyle: {
            p: 2,
            borderRadius: '18px',
            maxWidth: '70%',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }
        };
    }
  };

  const { justifyContent, messageStyle } = getMessageStyle();
  const timestamp = formatTimestamp(message.timestamp);

  return (
    <Box sx={{ display: 'flex', justifyContent, mb: 1, alignItems: 'flex-start' }}>
      {message.role !== 'user' && (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            mr: 1,
            bgcolor: getAvatarColor(),
            fontSize: '0.875rem'
          }}
        >
          {getMessageIcon()}
        </Avatar>
      )}

      <Box>
        {message.role === 'user' ? (
          <Box sx={{ display: 'flex', justifyContent }}>
            <Box sx={messageStyle}>
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'normal',
                  lineHeight: 1.6,
                  fontSize: '0.95rem',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  '& p': {
                    margin: 0
                  }
                }}
              >
                {message.content}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box>
            <Box sx={messageStyle}>
              <Box
                sx={{
                  lineHeight: 1.6,
                  fontSize: '0.95rem',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  position: 'relative',
                  '& p': {
                    margin: '0.5em 0',
                    '&:first-of-type': {
                      marginTop: 0
                    },
                    '&:last-of-type': {
                      marginBottom: 0
                    }
                  },
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    margin: '1em 0 0.5em 0',
                    lineHeight: 1.4
                  },
                  '& h1': {
                    fontSize: '1.5em',
                    fontWeight: 600
                  },
                  '& h2': {
                    fontSize: '1.3em',
                    fontWeight: 600
                  },
                  '& h3': {
                    fontSize: '1.1em',
                    fontWeight: 600
                  },
                  '& ul, & ol': {
                    margin: '0.5em 0',
                    paddingLeft: '1.5em'
                  },
                  '& li': {
                    margin: '0.25em 0'
                  },
                  '& code': {
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    padding: '0.2em 0.4em',
                    borderRadius: '3px',
                    fontSize: '0.9em',
                    fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace'
                  },
                  '& pre': {
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    padding: '1em',
                    borderRadius: '4px',
                    margin: '0.5em 0',
                    overflow: 'auto',
                    '& code': {
                      backgroundColor: 'transparent',
                      padding: 0
                    }
                  },
                  '& blockquote': {
                    margin: '0.5em 0',
                    paddingLeft: '1em',
                    borderLeft: '4px solid rgba(0, 0, 0, 0.1)',
                    fontStyle: 'italic'
                  },
                  '& table': {
                    borderCollapse: 'collapse',
                    margin: '0.5em 0',
                    width: '100%'
                  },
                  '& th, & td': {
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    padding: '0.5em',
                    textAlign: 'left'
                  },
                  '& th': {
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    fontWeight: 600
                  },
                  '& a': {
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }
                }}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    code: ({ className, children, ...props }: any) => {
                      const match = /language-(\w+)/.exec(className || '');
                      const isInline = !match;
                      return !isInline ? (
                        <pre>
                          <code className={className} {...props}>
                            {children}
                          </code>
                        </pre>
                      ) : (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      );
                    }
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </Box>
            </Box>

            {/* 保存为日志按钮 - 在AI消息气泡外面，气泡下方 */}
            {message.role === 'assistant' && onSaveFact && (
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-start' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PushPinIcon />}
                  onClick={() => onSaveFact(message.content)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  保存为日志
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* 时间戳 */}
        {showTimestamp && timestamp && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              color: 'text.secondary',
              fontSize: '0.75rem',
              textAlign: message.role === 'user' ? 'right' : 'left'
            }}
          >
            {timestamp}
          </Typography>
        )}
      </Box>

      {message.role === 'user' && (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            ml: 1,
            bgcolor: getAvatarColor(),
            fontSize: '0.875rem'
          }}
        >
          {getMessageIcon()}
        </Avatar>
      )}
    </Box>
  );
};

export default ChatMessage;